import { useRef, useState } from "react";
import styles from "./PhotoGallery.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import { IconPhoto } from "@tabler/icons-react";
// @ts-expect-error pure js
import Photo from "../UI/Photo";
// @ts-expect-error pure js
import { DocType } from "../../utils/constants/documents";
import Text from "../UI/Text.tsx";

interface PropertyPhotoData {
  id: number;
  src: string;
  position: number;
  is_main: boolean;
}

const DEFAULT_PHOTOS: Record<string, string> = {
  habitation: "/default-photo-appartement.jpeg",
  immeuble: "/default-photo-immeuble.jpeg",
  commercial: "/default-photo-commercial.jpeg",
  garage: "/default_garage.jpg",
  folder: "/default-photo-immeuble.jpeg",
};

interface PhotoGalleryProps {
  photos: PropertyPhotoData[];
  onPlaceholderClick?: () => void;
  propertyType?: string;
}

export default function PhotoGallery({
  photos = [],
  onPlaceholderClick,
  propertyType,
}: PhotoGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.offsetWidth;
    const newIndex = Math.round(scrollLeft / itemWidth);
    if (
      newIndex !== selectedIndex &&
      newIndex >= 0 &&
      newIndex < photos.length
    ) {
      setSelectedIndex(newIndex);
    }
  };

  if (photos.length === 0) {
    const defaultPhoto = propertyType && DEFAULT_PHOTOS[propertyType];
    if (defaultPhoto) {
      return (
        <div className={styles.desktopView}>
          <div
            className={classnames(
              styles.defaultPhotoWrapper,
              onPlaceholderClick && styles.placeholderClickable,
            )}
            onClick={onPlaceholderClick}
          >
            <img
              src={defaultPhoto}
              alt="Photo par défaut"
              className={styles.defaultPhoto}
            />
          </div>
          <div
            className={classnames(
              styles.addPhotoPanel,
              styles.placeholderClickable,
            )}
            onClick={onPlaceholderClick}
          >
            <IconPhoto size={32} />
            <Text as="span" variant="deci">
              Ajouter une photo
            </Text>
          </div>
        </div>
      );
    }
    return (
      <div
        className={classnames(
          styles.placeholder,
          onPlaceholderClick && styles.placeholderClickable,
        )}
        onClick={onPlaceholderClick}
      >
        <IconPhoto size={48} />
      </div>
    );
  }

  const showAddTile = !!onPlaceholderClick && photos.length < 5;
  const addTileGridSpan = (() => {
    switch (photos.length) {
      case 1:
        return { gridColumn: "1 / -1", gridRow: "1 / -1" };
      case 2:
        return { gridColumn: "2 / -1", gridRow: "1 / -1" };
      case 3:
        return { gridColumn: "1 / -1", gridRow: "2 / -1" };
      default:
        return undefined;
    }
  })();

  return (
    <>
      {/* Desktop view */}
      <div className={styles.desktopView}>
        {photos.length > 0 && (
          <Photo
            photoId={photos[0].id}
            type={DocType.PROPERTY_PHOTO}
            alt="Photo principale du bien"
            className={styles.mainPhoto}
            cover
          />
        )}

        {(photos.length > 1 || showAddTile) && (
          <div className={styles.secondaryPhotos}>
            {photos.slice(1, 5).map((photo, i) => (
              <Photo
                key={i}
                photoId={photo.id}
                type={DocType.PROPERTY_PHOTO}
                alt={`Photo du bien ${i + 2}`}
                className={styles.secondaryPhoto}
                isThumbnail
                cover
              />
            ))}
            {showAddTile && (
              <div
                className={classnames(
                  styles.addPhotoPanel,
                  styles.placeholderClickable,
                )}
                onClick={onPlaceholderClick}
                style={addTileGridSpan}
              >
                <IconPhoto size={32} />
                <Text as="span" variant="deci">
                  Ajouter une photo
                </Text>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile view - horizontal scroll */}
      <div className={styles.mobileView}>
        <div
          ref={scrollContainerRef}
          className={styles.mobileScroll}
          onScroll={handleScroll}
        >
          {photos.map((photo, i) => (
            <Photo
              key={i}
              photoId={photo.id}
              type={DocType.PROPERTY_PHOTO}
              alt={`Photo du bien ${i + 1}`}
              className={styles.mobileSlide}
              cover
            />
          ))}
        </div>
        {photos.length > 1 && (
          <div className={styles.dots}>
            {photos.map((_, index) => (
              <span
                key={index}
                className={classnames(
                  styles.dot,
                  index === selectedIndex && styles.dotActive,
                )}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
